/* eslint-disable camelcase */
import { omitBy } from 'lodash';

import type { Client, Events, Plugin } from '@coursera/event-pulse/core';

const EMPTY = 'EMPTY';
const PARAMETERS = [
  'campaignid',
  'creativeid',
  'keyword',
  'q',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
] as const;

type Campaign = Record<(typeof PARAMETERS)[number], string>;

type CampaignEventValue = Events['identify'];

/**
 * Get campaign parameters from the URL.
 */
function getCampaignParameters(): Campaign {
  const searchParams = new URLSearchParams(window.location.search);

  return PARAMETERS.reduce((campaign, param) => {
    const value = searchParams.get(param);

    return { ...campaign, [param]: value };
  }, {} as Campaign);
}

/**
 * This hook is called before page view event and detects if there are any new campaign parameters
 * in the URL. If there are, it sends an identify event with the new campaign parameters.
 *
 * @param client
 */
function onBeforeSendFactory(client: Client) {
  return async (eventName: keyof Events) => {
    if (eventName !== 'view_page') {
      // We only want to track campaign parameters on page view events.
      return;
    }
    const parameters = getCampaignParameters();

    const campaign = omitBy(
      {
        semCampaignId: parameters.campaignid,
        semCreativeId: parameters.creativeid,
        semKeyword: parameters.keyword,
        seoKeyword: parameters.q,
        utm_campaign: parameters.utm_campaign,
        utm_content: parameters.utm_content,
        utm_medium: parameters.utm_medium,
        utm_source: parameters.utm_source,
        utm_term: parameters.utm_term,
      } satisfies CampaignEventValue['set'],
      (value) => !value
    );

    if (Object.keys(campaign).length === 0) {
      // There is no new campaign parameters to send.
      return;
    }

    // We cannot rely on `identify` function as it's using high client to send the events
    await client.sendEvent('identify', { set: campaign });
  };
}

/**
 * This hook is called when a new session starts and sends an identify event with the first touch values.
 * It's important to note that this event is sent only once per session.
 *
 * @param client
 */
function setInitialTouchFactory(client: Client) {
  return async () => {
    const parameters = getCampaignParameters();

    // We cannot rely on `identify` function as it's using high client to send the events
    await client.sendEvent('identify', {
      setOnce: {
        initialSemCampaignId: parameters.campaignid || EMPTY,
        initialSemCreativeId: parameters.creativeid || EMPTY,
        initialSemKeyword: parameters.keyword || EMPTY,
        initialSeoKeyword: parameters.q || EMPTY,
        initial_referrer_url: window.document.referrer || EMPTY,
        initial_utm_campaign: parameters.utm_campaign || EMPTY,
        initial_utm_content: parameters.utm_content || EMPTY,
        initial_utm_medium: parameters.utm_medium || EMPTY,
        initial_utm_source: parameters.utm_source || EMPTY,
        initial_utm_term: parameters.utm_term || EMPTY,
      },
    });
  };
}

/**
 * This hook is called when a session ends and resets the campaign parameters.
 *
 * @param client
 */
function unsetPreviousCampaignFactory(client: Client) {
  return async () => {
    // We cannot rely on `identify` function as it's using high client to send the events
    await client.sendEvent('identify', {
      unset: {
        semCampaignId: EMPTY,
        semCreativeId: EMPTY,
        semKeyword: EMPTY,
        seoKeyword: EMPTY,
        utm_campaign: EMPTY,
        utm_content: EMPTY,
        utm_medium: EMPTY,
        utm_source: EMPTY,
        utm_term: EMPTY,
      },
    });
  };
}

const plugin: Plugin = (client) => {
  client.on('beforeSend', onBeforeSendFactory(client));
  client.on('sessionStart', unsetPreviousCampaignFactory(client));
  client.on('sessionStart', setInitialTouchFactory(client));
};

export type { Campaign };
export { getCampaignParameters, plugin };
